import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@personic/shared'

// ─── Types ──────────────────────────────────────────────────────────

export interface SignalBandThresholds {
  low: number
  moderate_low: number
  moderate_high: number
  high: number
}

export interface SignalBandingSettings {
  certainty: SignalBandThresholds
  commitment: SignalBandThresholds
  hedging: SignalBandThresholds
  emotion: SignalBandThresholds
  formality: SignalBandThresholds
  directness: SignalBandThresholds
  engagement: SignalBandThresholds
}

export interface ConfidenceBandThresholds {
  low: number
  moderate: number
}

export interface DeviationSeverityThresholds {
  slight: number
  noticeable: number
  significant: number
}

export interface TemporalSettings {
  short_term_window: number
  baseline_establishment_turns: number
  min_turns_for_temporal: number
  stream_min_turns: number
  profile_lookback_sessions: number
}

export interface SignalSensitivitySettings {
  deviation_threshold_conversation: number
  deviation_threshold_profile: number
  deviation_threshold_zscore: number
  inflection_threshold: number
  trend_threshold_slope: number
}

export interface CoachingNudgeSettings {
  max_nudges_per_turn: number
  min_turns_between_same_module: number
  max_nudges_per_conversation: number
  baseline_smoothing_alpha: number
}

export interface ModuleToggles {
  clarity: boolean
  commitment: boolean
  deescalation: boolean
  negotiation: boolean
  congruence: boolean
  comprehension: boolean
}

export interface PimSettings {
  banding: SignalBandingSettings
  confidence: ConfidenceBandThresholds
  deviation_severity: DeviationSeverityThresholds
  temporal: TemporalSettings
  sensitivity: SignalSensitivitySettings
  nudges: CoachingNudgeSettings
  modules: ModuleToggles
}

export type SettingSource = 'default' | 'tenant' | 'user'

export interface PimSettingsResponse {
  tenant_id?: string
  tenant_name?: string
  settings: PimSettings
  sources: Record<string, SettingSource>
}

// ─── Hooks ──────────────────────────────────────────────────────────

export function useTenantPimSettings(tenantId: string | undefined) {
  return useQuery<PimSettingsResponse>({
    queryKey: ['admin', 'pim-settings', tenantId],
    queryFn: async () => {
      const { data } = await api.get(`/admin/tenants/${tenantId}/pim-settings`)
      return data
    },
    enabled: !!tenantId,
  })
}

export function useUpdateTenantPimSettings(tenantId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (updates: Partial<PimSettings>) => {
      const { data } = await api.put(`/admin/tenants/${tenantId}/pim-settings`, updates)
      return data as PimSettingsResponse
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'pim-settings', tenantId] })
    },
  })
}

export function useResetTenantPimSettings(tenantId: string | undefined) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.delete(`/admin/tenants/${tenantId}/pim-settings`)
      return data as PimSettingsResponse
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'pim-settings', tenantId] })
    },
  })
}

// ─── Validation Ranges (match backend Pydantic schema) ─────────────

export const PIM_RANGES = {
  banding: {
    low: { min: 0, max: 1, step: 0.05 },
    moderate_low: { min: 0, max: 1, step: 0.05 },
    moderate_high: { min: 0, max: 1, step: 0.05 },
    high: { min: 0, max: 1, step: 0.05 },
  },
  confidence: {
    low: { min: 0, max: 1, step: 0.05 },
    moderate: { min: 0, max: 1, step: 0.05 },
  },
  deviation_severity: {
    slight: { min: 0.1, max: 2.0, step: 0.1 },
    noticeable: { min: 0.3, max: 2.5, step: 0.1 },
    significant: { min: 1.0, max: 4.0, step: 0.1 },
  },
  temporal: {
    short_term_window: { min: 3, max: 50, step: 1 },
    baseline_establishment_turns: { min: 10, max: 100, step: 5 },
    min_turns_for_temporal: { min: 3, max: 20, step: 1 },
    stream_min_turns: { min: 2, max: 10, step: 1 },
    profile_lookback_sessions: { min: 3, max: 50, step: 1 },
  },
  sensitivity: {
    deviation_threshold_conversation: { min: 0.05, max: 0.5, step: 0.01 },
    deviation_threshold_profile: { min: 0.05, max: 0.5, step: 0.01 },
    deviation_threshold_zscore: { min: 0.5, max: 3.0, step: 0.1 },
    inflection_threshold: { min: 0.1, max: 0.8, step: 0.05 },
    trend_threshold_slope: { min: 0.01, max: 0.2, step: 0.01 },
  },
  nudges: {
    max_nudges_per_turn: { min: 0, max: 3, step: 1 },
    min_turns_between_same_module: { min: 1, max: 10, step: 1 },
    max_nudges_per_conversation: { min: 0, max: 100, step: 5 },
    baseline_smoothing_alpha: { min: 0.1, max: 0.9, step: 0.05 },
  },
} as const

// Human-friendly labels
export const SETTING_LABELS: Record<string, Record<string, string>> = {
  banding: {
    low: 'Low threshold',
    moderate_low: 'Moderate-low threshold',
    moderate_high: 'Moderate-high threshold',
    high: 'High threshold',
  },
  temporal: {
    short_term_window: 'Short-term window (turns)',
    baseline_establishment_turns: 'Baseline establishment (turns)',
    min_turns_for_temporal: 'Min turns for temporal analysis',
    stream_min_turns: 'Min per-stream turns',
    profile_lookback_sessions: 'Profile lookback sessions',
  },
  sensitivity: {
    deviation_threshold_conversation: 'Conversation deviation threshold',
    deviation_threshold_profile: 'Profile deviation threshold',
    deviation_threshold_zscore: 'Z-score threshold',
    inflection_threshold: 'Inflection detection threshold',
    trend_threshold_slope: 'Trend slope threshold',
  },
  nudges: {
    max_nudges_per_turn: 'Max nudges per turn',
    min_turns_between_same_module: 'Cooldown between same module',
    max_nudges_per_conversation: 'Max nudges per conversation',
    baseline_smoothing_alpha: 'Baseline smoothing factor',
  },
}

export const SIGNAL_NAMES = [
  'certainty', 'commitment', 'hedging', 'emotion',
  'formality', 'directness', 'engagement',
] as const

export const SIGNAL_LABELS: Record<string, string> = {
  certainty: 'Certainty',
  commitment: 'Commitment',
  hedging: 'Hedging',
  emotion: 'Emotion',
  formality: 'Formality',
  directness: 'Directness',
  engagement: 'Engagement',
}

export const MODULE_LABELS: Record<string, string> = {
  clarity: 'Clarity',
  commitment: 'Commitment',
  deescalation: 'De-escalation',
  negotiation: 'Negotiation',
  congruence: 'Congruence',
  comprehension: 'Comprehension',
}
